/**
 * ProssimaSfida — la sfida (o LE sfide) della mia squadra nella prossima
 * giornata, con sotto il countdown per consegnare la formazione.
 *
 * Andrea 2026-09-09: quando nella stessa giornata si gioca campionato E
 * Champions il blocco diventa sfogliabile — frecce ai lati della card,
 * una competizione per volta. Anche il countdown segue la competizione
 * mostrata: la formazione si consegna per ognuna, quindi può essere
 * consegnata per il campionato e mancare in coppa (e viceversa).
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useFormazione } from "../../hooks/useFase4";
import {
  usePartiteCompetizione,
  useSimulazioneLive,
  type CompetizioneItem,
  type PartitaItem,
} from "../../hooks/useFase5";
import { useRegolamento } from "../../hooks/useStagione";
import { useSwipe } from "../../hooks/useSwipe";
import { FormazioneCountdown } from "./FormazioneCountdown";
import { MatchCard, type PunteggioLive } from "./MatchCard";
import { progressoGol } from "./PalloniGol";
import { tr } from "../../i18n";

interface GiornataMini {
  id: string;
  numero: number;
  numero_serie_a: number;
}

interface ProssimaSfidaProps {
  squadraId: string;
  giornata: GiornataMini | null;
  /** Competizioni ancora in corso della stagione (max 3: campionato, CL, supercoppa). */
  competizioni: CompetizioneItem[];
  nomeSquadra: (id: string) => string;
  logoSquadra: (id: string) => string | null;
  /** Cosa scrivere se non c'è nessuna sfida da mostrare. */
  messaggioVuoto: string;
  /** Andrea 2026-09-11: "live" = la giornata è in corso (turno partito,
   * non calcolata): stessa card, ma con "Vai al live" al posto del
   * countdown, e se la squadra non gioca non si mostra niente. */
  modalita?: "prossima" | "live";
  /** Andrea 2026-09-17: c'e' una giornata in corso, quindi questa e' la
   * partita DOPO. Il blocco va "spento" (si capisce a colpo d'occhio che
   * non e' quella che si sta giocando) e non si invita a inserire la
   * formazione: Schiera resta sulla giornata in corso finche' non viene
   * calcolata. */
  inAttesa?: boolean;
}

/** La partita della squadra in quella giornata, se c'è. */
function miaPartita(
  partite: PartitaItem[] | undefined,
  squadraId: string,
  giornataId: string | undefined,
): PartitaItem | null {
  if (!partite || !giornataId) return null;
  return (
    partite.find(
      (p) =>
        p.giornata_id === giornataId &&
        (p.squadra_casa_id === squadraId || p.squadra_trasferta_id === squadraId),
    ) ?? null
  );
}

export function ProssimaSfida({
  squadraId,
  giornata,
  competizioni,
  nomeSquadra,
  logoSquadra,
  messaggioVuoto,
  modalita = "prossima",
  inAttesa = false,
}: ProssimaSfidaProps) {
  const live = modalita === "live";
  const spenta = inAttesa && !live;
  // Numero di hook fisso (le regole dei hook non ammettono cicli): le
  // competizioni contemporanee sono al massimo tre — campionato, coppa,
  // supercoppa.
  const [compA, compB, compC] = [
    competizioni[0] ?? null,
    competizioni[1] ?? null,
    competizioni[2] ?? null,
  ];
  const { data: partiteA } = usePartiteCompetizione(compA?.id ?? null);
  const { data: partiteB } = usePartiteCompetizione(compB?.id ?? null);
  const { data: partiteC } = usePartiteCompetizione(compC?.id ?? null);
  const { data: formazioneA } = useFormazione(squadraId, giornata?.id ?? null, compA?.id ?? null);
  const { data: formazioneB } = useFormazione(squadraId, giornata?.id ?? null, compB?.id ?? null);
  const { data: formazioneC } = useFormazione(squadraId, giornata?.id ?? null, compC?.id ?? null);

  const sfide = [
    {
      comp: compA,
      partita: miaPartita(partiteA, squadraId, giornata?.id),
      formazione: formazioneA,
    },
    {
      comp: compB,
      partita: miaPartita(partiteB, squadraId, giornata?.id),
      formazione: formazioneB,
    },
    {
      comp: compC,
      partita: miaPartita(partiteC, squadraId, giornata?.id),
      formazione: formazioneC,
    },
  ].filter(
    (
      s,
    ): s is {
      comp: CompetizioneItem;
      partita: PartitaItem;
      formazione: typeof formazioneA;
    } => s.comp !== null && s.partita !== null,
  );

  const [indice, setIndice] = useState(0);
  // Se cambia il numero di sfide (arriva la coppa, finisce il campionato)
  // non restiamo puntati su un indice che non esiste più.
  useEffect(() => {
    setIndice((i) => (i < sfide.length ? i : 0));
  }, [sfide.length]);

  const corrente = sfide[indice] ?? null;

  // Christian 2026-11-03: a giornata in corso, risultato provvisorio col
  // motore vero (voti live+finali, 6 d'ufficio a chi deve giocare) e
  // palloni verso il prossimo gol.
  const { data: simulazione } = useSimulazioneLive(live ? (corrente?.partita.id ?? null) : null);
  const { data: regolamento } = useRegolamento(live ? (corrente?.comp.stagione_id ?? null) : null);
  const punteggioLive: PunteggioLive | null =
    live && simulazione && simulazione.partita_id === corrente?.partita.id
      ? {
          golCasa: simulazione.casa.gol,
          golTrasferta: simulazione.trasferta.gol,
          puntiCasa: simulazione.casa.punteggio_totale,
          puntiTrasferta: simulazione.trasferta.punteggio_totale,
          progressoCasa: regolamento
            ? progressoGol(simulazione.casa.punteggio_totale, regolamento.soglie_gol)
            : null,
          progressoTrasferta: regolamento
            ? progressoGol(simulazione.trasferta.punteggio_totale, regolamento.soglie_gol)
            : null,
        }
      : null;
  // Le altre competizioni di questa giornata senza formazione consegnata.
  const altreMancanti = sfide
    .map((s, i) => ({ s, i }))
    .filter(({ s, i }) => i !== indice && !s.formazione?.consegnata_at);
  const piuSfide = sfide.length > 1;
  const vaiA = (delta: number) => setIndice((i) => (i + delta + sfide.length) % sfide.length);
  // Andrea 2026-09-09: stesso gesto della card classifica — si sfoglia
  // trascinando, le frecce restano per chi usa il mouse.
  const swipe = useSwipe({
    onAvanti: () => vaiA(1),
    onIndietro: () => vaiA(-1),
    attivo: piuSfide,
  });

  // In corso ma la squadra non gioca in questa giornata (coppa a
  // gironi con turno di riposo): niente blocco.
  if (live && !corrente) return null;

  return (
    <div
      className={spenta ? "opacity-60 saturate-50 transition" : undefined}
      title={spenta ? tr("Si gioca dopo la giornata in corso") : undefined}
    >
      {/* Christian 2026-11-03: titolo e competizione sempre interi su una
          riga: su telefono si rimpiccioliscono invece di andare a capo. */}
      <div className="flex justify-between items-baseline gap-2 mb-2 whitespace-nowrap">
        <h3
          className={`font-display text-[15px] sm:text-lg tracking-wide sm:tracking-wider flex items-center gap-1.5 sm:gap-2 ${
            spenta ? "text-fg-muted" : "text-fg"
          }`}
        >
          {live && (
            <span
              className="inline-block w-2.5 h-2.5 rounded-full bg-danger animate-pulse"
              aria-hidden="true"
            />
          )}
          {live ? tr("GIORNATA IN CORSO") : tr("PROSSIMA SFIDA")}
        </h3>
        {corrente && (
          <span className="text-[9px] sm:text-[10px] text-fg-muted uppercase font-mono">
            {corrente.comp.nome}
            {piuSfide ? tr(" · {{v1}}/{{v2}}", { v1: indice + 1, v2: sfide.length }) : ""}
          </span>
        )}
      </div>

      {corrente ? (
        <div className="relative">
          <div
            {...swipe.handlers}
            className="touch-pan-y select-none"
            style={{
              transform: `translateX(${swipe.offset}px)`,
              transition: swipe.inTrascinamento ? "none" : "transform 150ms",
            }}
          >
            <Link
              to={`/partita/${corrente.partita.id}`}
              draggable={false}
              onClick={swipe.annullaClick}
              className="block hover:scale-[1.01] transition"
              title={tr("Apri dettaglio partita (formazioni + voti live)")}
            >
              <MatchCard
                competizione={corrente.comp.nome}
                data={
                  giornata
                    ? `Giornata ${giornata.numero} · SA ${giornata.numero_serie_a}${
                        live ? " · " + tr("in corso") : ""
                      }`
                    : "—"
                }
                homeTeam={{
                  nome: nomeSquadra(corrente.partita.squadra_casa_id),
                  logo_data_url: logoSquadra(corrente.partita.squadra_casa_id),
                }}
                awayTeam={{
                  nome: nomeSquadra(corrente.partita.squadra_trasferta_id),
                  logo_data_url: logoSquadra(corrente.partita.squadra_trasferta_id),
                }}
                live={punteggioLive}
              />
            </Link>
          </div>

          {/* Frecce: fuori dal <Link> (un bottone dentro un link non si
              può). Christian 2026-11-03: minimal, solo il segno, al bordo
              della card all'altezza degli stemmi (81px = padding + riga
              competizione + meta' stemma), cosi' non coprono nomi e punti. */}
          {piuSfide && (
            <>
              <button
                type="button"
                onClick={() => vaiA(-1)}
                aria-label={tr("Sfida precedente")}
                title={tr("Sfida precedente")}
                className="absolute left-0 top-[81px] -translate-y-1/2 w-7 h-10 flex items-center justify-center font-display text-3xl leading-none text-accent/80 hover:text-accent transition"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={() => vaiA(1)}
                aria-label={tr("Sfida successiva")}
                title={tr("Sfida successiva")}
                className="absolute right-0 top-[81px] -translate-y-1/2 w-7 h-10 flex items-center justify-center font-display text-3xl leading-none text-accent/80 hover:text-accent transition"
              >
                ›
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center">
          <div className="text-4xl mb-2">⚽</div>
          <div className="text-fg-muted text-sm">{messaggioVuoto}</div>
        </div>
      )}

      {/* Pallini competizione: dicono quante sfide ci sono e dove siamo. */}
      {piuSfide && (
        <div className="flex justify-center gap-1.5 mt-2">
          {sfide.map((s, i) => (
            <button
              key={s.comp.id}
              type="button"
              onClick={() => setIndice(i)}
              aria-label={s.comp.nome}
              title={`${s.comp.nome}${
                s.formazione?.consegnata_at ? "" : " — " + tr("formazione non ancora consegnata")
              }`}
              className={`h-1.5 rounded-full transition ${
                i === indice
                  ? "w-6 bg-accent"
                  : s.formazione?.consegnata_at
                    ? "w-3 bg-fg-muted/40"
                    : "w-3 bg-warning/70"
              }`}
            />
          ))}
        </div>
      )}

      {/* Live: bottone Vai al live + com'è andata la consegna, niente
          countdown (la formazione è bloccata). */}
      {live && corrente && (
        <div className="mt-2">
          <Link
            to={`/partita/${corrente.partita.id}`}
            className="block w-full text-center rounded-card px-4 py-3 bg-accent text-ink font-bold tracking-wide hover:opacity-90 transition"
          >
            {tr("▶ Vai al live")}
          </Link>
          <div
            className={`mt-1.5 text-center text-[11px] font-mono uppercase tracking-wider ${
              corrente.formazione?.consegnata_at ? "text-success" : "text-danger"
            }`}
          >
            {corrente.formazione?.consegnata_at
              ? tr("✓ Formazione consegnata")
              : tr("⚠ Formazione non consegnata · punteggio fisso")}
          </div>
        </div>
      )}

      {/* Andrea 2026-09-09: consegnare per il campionato non basta se c'e'
          anche la coppa — qui si vede subito quale manca ancora. */}
      {!live && !spenta && altreMancanti.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-card border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          <span>{tr("⚠ Formazione ancora da consegnare per:")}</span>
          {altreMancanti.map(({ s, i }) => (
            <button
              key={s.comp.id}
              type="button"
              onClick={() => setIndice(i)}
              className="font-bold underline underline-offset-2 hover:opacity-80"
            >
              {s.comp.nome}
            </button>
          ))}
        </div>
      )}

      {/* Countdown consegna: sempre riferito alla competizione mostrata. */}
      {!live && giornata && (
        <div className="mt-2">
          <FormazioneCountdown
            matchday={giornata.numero_serie_a}
            giornata={giornata.numero}
            consegnata={!!corrente?.formazione?.consegnata_at}
            inAttesa={spenta}
            // Con una sola competizione si va su Schiera com'era prima,
            // cioè con "Tutte" selezionato: puntare sulla singola farebbe
            // salvare solo quella senza che l'utente se ne accorga.
            competizioneId={piuSfide ? (corrente?.comp.id ?? null) : null}
            competizioneNome={piuSfide ? (corrente?.comp.nome ?? null) : null}
          />
        </div>
      )}
    </div>
  );
}
